import {
    Editor,
    Element,
    Node,
    Path,
    Transforms,
    type NodeEntry,
    type TableBodyElement,
} from "slate";

import {isElement, isElementOfType} from "../helpers/node-helpers.ts";

export const withTables = (editor: Editor) => {
    const {isBlock, normalizeNode} = editor;

    editor.isBlock = (element: Element) =>
        element.type === "table" || isBlock(element);

    editor.normalizeNode = (entry: NodeEntry, options) => {
        const [node, path] = entry;

        if (normalizeTable(editor, node, path)) {
            return;
        }

        if (normalizeTableHeaderOrBody(editor, node, path)) {
            return;
        }

        if (normalizeTableRow(editor, node, path)) {
            return;
        }

        if (normalizeTableCell(editor, node, path)) {
            return;
        }

        normalizeNode(entry, options);
    };

    return editor;
};

const normalizeTable = (editor: Editor, node: Node, path: Path) => {
    if (!isElementOfType(node, "table")) {
        // This check only applies to table elements!
        return false;
    }

    // Within a table element, we want to make sure that all child
    // nodes are wrapped in a table-header or table-body.
    for (const [child, childPath] of Node.children(editor, path)) {
        if (isElementOfType(child, "table-header", "table-body")) {
            continue;
        }

        const tbody = parentTableBody(editor, path);
        if (!tbody) {
            // If there's no parent tbody, wrap it now and return.
            Transforms.wrapNodes(
                editor,
                {type: "table-body", children: []},
                {at: childPath},
            );
            return true;
        }

        const [tbodyNode, tbodyPath] = tbody;

        const elements = tbodyNode.children.filter(
            (n) => isElement(n) && !Editor.isInline(editor, n),
        );

        // Move this child node into the parent tbody (at the end)
        Transforms.moveNodes(editor, {
            at: childPath,
            to: [...tbodyPath, elements.length],
        });
        return true;
    }

    return false;
};

const normalizeTableHeaderOrBody = (editor: Editor, node: Node, path: Path) => {
    if (!isElementOfType(node, "table-header", "table-body")) {
        // This check only applies to table-header and table-body elements!
        return false;
    }

    // Within a these elements, we want to make sure all children are
    // wrapped in table-row elements.
    for (const [child, childPath] of Node.children(editor, path)) {
        if (isElementOfType(child, "table-row")) {
            continue;
        }

        Transforms.wrapNodes(
            editor,
            {type: "table-row", children: []},
            {at: childPath},
        );
        return true;
    }

    return false;
};

const normalizeTableRow = (editor: Editor, node: Node, path: Path) => {
    if (!isElementOfType(node, "table-row")) {
        // This check only applies to table-row elements!
        return false;
    }

    // Within a table-row, we want to make sure all children are
    // wrapped in table-cell elements.
    for (const [child, childPath] of Node.children(editor, path)) {
        if (isElementOfType(child, "table-cell")) {
            continue;
        }

        Transforms.wrapNodes(
            editor,
            {type: "table-cell", children: []},
            {at: childPath},
        );
        return true;
    }

    return false;
};

const normalizeTableCell = (editor: Editor, node: Node, path: Path) => {
    if (!isElementOfType(node, "table-cell")) {
        // This check only applies to table-cell elements!
        return false;
    }

    let normalized = false;

    // Within a table-cell, we want to make sure all children are
    // inline elements.
    for (const [child, childPath] of Node.children(editor, path)) {
        if (Element.isElement(child) && Editor.isBlock(editor, child)) {
            Transforms.unwrapNodes(editor, {at: childPath});
            normalized = true;
        }
    }

    return normalized;
};

const parentTableBody = (
    editor: Editor,
    tablePath: Path,
): NodeEntry<TableBodyElement> | undefined => {
    const [tbody] = Editor.nodes<TableBodyElement>(editor, {
        match: (n) => isElementOfType(n, "table-body"),
        at: tablePath,
    });

    if (!tbody) {
        return undefined;
    }

    const [, path] = tbody;

    if (!Path.isChild(path, tablePath)) {
        return undefined;
    }

    return tbody;
};
